import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import { EditRosterOnlyDialog } from "@/components/RosterOnlyDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, FileDown, Eye, Send, Pencil } from "lucide-react";
import { StateZoneFields } from "@/components/StateZoneFields";
import { toast } from "sonner";

function EditDetailsButton({ emp }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="edit-details-btn"><Pencil className="mr-2 h-4 w-4" /> Edit details</Button>
      <EditRosterOnlyDialog emp={emp} open={open} onClose={() => setOpen(false)} onDone={() => qc.invalidateQueries()} />
    </>
  );
}

function InviteOnboardButton({ emp }) {
  const qc = useQueryClient();
  const [link, setLink] = useState("");
  const invite = useMutation({
    mutationFn: async () => (await api.post(`/org/employees/${emp.id}/invite-onboard`)).data,
    onSuccess: (d) => { toast.success(d.sent_status === "sent" ? "Onboarding invite emailed" : "Invite created (email not sent)"); setLink(d.link); qc.invalidateQueries(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <>
      <Button onClick={() => invite.mutate()} disabled={invite.isPending} data-testid="invite-onboard-btn"><Send className="mr-2 h-4 w-4" /> Invite to onboard</Button>
      <LinkDialog open={!!link} onClose={() => setLink("")} title="Onboarding invite link" note="Share this link with the employee." link={link} />
    </>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { actingOrgId, isManagerOnly: readOnly } = useAuth();
  const [diff, setDiff] = useState({ from: "", to: "", changes: null });
  const [decideCr, setDecideCr] = useState(null);
  const [comment, setComment] = useState("");

  const { data, refetch } = useQuery({
    queryKey: ["emp-form", id, actingOrgId],
    queryFn: async () => (await api.get(`/org/employees/${id}/form`)).data,
  });
  const { data: masters } = useQuery({
    queryKey: ["all-masters", actingOrgId],
    queryFn: async () => {
      const [d, g, l] = await Promise.all([
        api.get("/org/masters/departments"), api.get("/org/masters/designations"), api.get("/org/masters/locations")]);
      return { departments: d.data, designations: g.data, locations: l.data };
    },
  });

  const saveAdmin = useMutation({
    mutationFn: async (fields) => (await api.put(`/org/employees/${id}/admin-fields`, fields)).data,
    onSuccess: () => { toast.success("Saved"); refetch(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const setMode = useMutation({
    mutationFn: async (mode) => (await api.put(`/org/employees/${id}/statutory-mode`, { mode })).data,
    onSuccess: () => { toast.success("Statutory mode updated"); refetch(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decide = useMutation({
    mutationFn: async ({ crId, action }) => (await api.post(`/org/change-requests/${crId}/${action}`, { comment })).data,
    onSuccess: () => { toast.success("Done"); setDecideCr(null); setComment(""); refetch(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const runDiff = async () => {
    if (!diff.from || !diff.to) return;
    const { data: r } = await api.get(`/org/employees/${id}/diff`, { params: { frm: diff.from, to: diff.to } });
    setDiff({ ...diff, changes: r.changes });
  };

  const downloadPdf = async () => {
    const res = await api.get(`/org/employees/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "joining_form.pdf"; a.click();
    URL.revokeObjectURL(url);
  };

  const viewDoc = async (docId) => {
    const res = await api.get(`/org/documents/${docId}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  };

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  const emp = data.employee;
  const af = data.admin_fields;

  const renderData = (obj, prefix = "") => Object.entries(obj || {}).map(([k, v]) => {
    if (v && typeof v === "object") {
      return <div key={prefix + k} className="mt-1"><p className="text-xs font-semibold text-slate-500">{k}</p><div className="pl-3">{renderData(Array.isArray(v) ? Object.fromEntries(v.map((x, i) => [i, x])) : v, prefix + k + ".")}</div></div>;
    }
    return <div key={prefix + k} className="flex gap-2 text-xs"><span className="min-w-[160px] text-slate-400">{k}</span><span className="text-slate-700">{String(v)}</span></div>;
  });

  return (
    <div>
      <button onClick={() => navigate("/org/employees")} className="mb-4 flex items-center gap-1 text-sm text-slate-500" data-testid="back-employees">
        <ArrowLeft className="h-4 w-4" /> Employees
      </button>
      <PageHeader title={emp.name} description={emp.email}
        actions={<div className="flex flex-wrap gap-2">
          {emp.roster_only && <EditDetailsButton emp={emp} />}
          {emp.roster_only && !readOnly && emp.email && <InviteOnboardButton emp={emp} />}
          <Button variant="outline" onClick={downloadPdf} disabled={!emp.current_version} data-testid="admin-download-pdf"><FileDown className="mr-2 h-4 w-4" /> PDF</Button>
        </div>} />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <StatusBadge status={data.form_status} />
        <span className="text-xs text-slate-400">Version {emp.current_version || "—"} · Consent {data.consent_at ? "given" : "pending"}</span>
      </div>

      <Tabs defaultValue="data">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="data" data-testid="tab-data">Current data</TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions">Versions & diff</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Documents</TabsTrigger>
          <TabsTrigger value="cr" data-testid="tab-cr">Change requests</TabsTrigger>
          <TabsTrigger value="admin" data-testid="tab-admin">Admin fields</TabsTrigger>
        </TabsList>

        <TabsContent value="data">
          <Card className="space-y-1 p-5" data-testid="current-data">{renderData(data.current_data)}</Card>
        </TabsContent>

        <TabsContent value="versions">
          <Card className="p-5">
            <div className="mb-3 space-y-1">
              {data.versions.map((v) => (
                <div key={v.version} className="flex items-center justify-between text-sm" data-testid={`version-${v.version}`}>
                  <span>v{v.version} · {v.submitted_at_ist} · {v.location_text}</span>
                </div>
              ))}
              {!data.versions.length && <p className="text-sm text-slate-400">No submitted versions yet.</p>}
            </div>
            {data.versions.length >= 2 && (
              <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
                <div><Label className="text-xs">From</Label>
                  <Select value={String(diff.from)} onValueChange={(v) => setDiff({ ...diff, from: v })}>
                    <SelectTrigger className="h-8 w-20" data-testid="diff-from"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.versions.map((v) => <SelectItem key={v.version} value={String(v.version)}>v{v.version}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">To</Label>
                  <Select value={String(diff.to)} onValueChange={(v) => setDiff({ ...diff, to: v })}>
                    <SelectTrigger className="h-8 w-20" data-testid="diff-to"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.versions.map((v) => <SelectItem key={v.version} value={String(v.version)}>v{v.version}</SelectItem>)}</SelectContent>
                  </Select></div>
                <Button size="sm" onClick={runDiff} data-testid="run-diff">Compare</Button>
              </div>
            )}
            {diff.changes && (
              <div className="mt-3 space-y-1" data-testid="diff-result">
                {diff.changes.length === 0 ? <p className="text-sm text-slate-400">No differences.</p> :
                  diff.changes.map((c, i) => (
                    <div key={i} className="text-xs"><span className="font-medium text-slate-600">{c.field}</span>: <span className="text-rose-500 line-through">{String(c.from ?? "")}</span> → <span className="text-emerald-600">{String(c.to ?? "")}</span></div>
                  ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="docs">
          <Card className="divide-y divide-slate-100">
            {data.documents.length ? data.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-5 py-3" data-testid={`doc-${d.field}`}>
                <span className="text-sm">{d.field} <span className="text-xs text-slate-400">({d.content_type})</span></span>
                <Button size="sm" variant="ghost" onClick={() => viewDoc(d.id)} data-testid={`view-doc-${d.field}`}><Eye className="mr-1 h-4 w-4" /> View</Button>
              </div>
            )) : <p className="px-5 py-6 text-center text-sm text-slate-400">No documents uploaded.</p>}
          </Card>
        </TabsContent>

        <TabsContent value="cr">
          <Card className="divide-y divide-slate-100">
            {data.change_requests.length ? data.change_requests.map((cr) => (
              <div key={cr.id} className="px-5 py-3" data-testid={`cr-${cr.id}`}>
                <div className="flex items-center justify-between">
                  <div><p className="text-sm">Sections: {(cr.sections || []).join(", ")}</p><p className="text-xs text-slate-500">{cr.reason}</p></div>
                  <StatusBadge status={cr.status} />
                </div>
                {cr.comment && <p className="mt-1 text-xs text-slate-500">Comment: {cr.comment}</p>}
                {cr.status === "pending" && !readOnly && (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={() => setDecideCr({ cr, action: "approve" })} data-testid={`approve-cr-${cr.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => setDecideCr({ cr, action: "reject" })} data-testid={`reject-cr-${cr.id}`}>Reject</Button>
                  </div>
                )}
              </div>
            )) : <p className="px-5 py-6 text-center text-sm text-slate-400">No change requests.</p>}
          </Card>
        </TabsContent>

        <TabsContent value="admin">
          <Card className="space-y-4 p-5">
            <div>
              <Label className="text-xs">Statutory mode {data.form_status === "submitted" && "(locked after submit)"}</Label>
              <Select value={data.statutory_mode} onValueChange={(v) => setMode.mutate(v)} disabled={readOnly || data.form_status === "submitted"}>
                <SelectTrigger className="mt-1 max-w-xs" data-testid="admin-statutory-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ask">Ask employee</SelectItem>
                  <SelectItem value="with_statutory">With statutory</SelectItem>
                  <SelectItem value="without_statutory">Without statutory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <AdminFields af={af} masters={masters} readOnly={readOnly} onSave={(f) => saveAdmin.mutate(f)} />
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!decideCr} onOpenChange={(v) => !v && setDecideCr(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decideCr?.action === "approve" ? "Approve" : "Reject"} change request</DialogTitle></DialogHeader>
          <Textarea placeholder="Comment" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="cr-comment" />
          <DialogFooter>
            <Button onClick={() => decide.mutate({ crId: decideCr.cr.id, action: decideCr.action })} data-testid="cr-decide-submit">
              {decideCr?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminFields({ af, masters, onSave, readOnly }) {
  const [f, setF] = useState({ ...af, state_code: af.state_code || "", zone_id: af.zone_id || "" });
  const upd = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <fieldset disabled={readOnly} className="grid gap-3 md:grid-cols-2">
      <div><Label className="text-xs">Employee code</Label><Input value={f.employee_code || ""} onChange={(e) => upd("employee_code")(e.target.value)} className="mt-1" data-testid="af-code" /></div>
      <div><Label className="text-xs">Date of joining</Label><Input value={f.date_of_joining || ""} onChange={(e) => upd("date_of_joining")(e.target.value)} className="mt-1" data-testid="af-doj" /></div>
      {[["department_id", "Department", "departments"], ["designation_id", "Designation", "designations"], ["location_id", "Location", "locations"]].map(([k, lab, mk]) => (
        <div key={k}><Label className="text-xs">{lab}</Label>
          <Select value={f[k] || ""} onValueChange={upd(k)} disabled={readOnly}>
            <SelectTrigger className="mt-1" data-testid={`af-${mk}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{(masters?.[mk] || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select></div>
      ))}
      <div><Label className="text-xs">Authorised signatory</Label><Input value={f.employer_signatory_name || ""} onChange={(e) => upd("employer_signatory_name")(e.target.value)} className="mt-1" data-testid="af-signatory" /></div>
      <div><Label className="text-xs">Employer place</Label><Input value={f.employer_place || ""} onChange={(e) => upd("employer_place")(e.target.value)} className="mt-1" data-testid="af-place" /></div>
      <StateZoneFields value={{ state_code: f.state_code || "", zone_id: f.zone_id || "" }} onChange={(v) => setF({ ...f, ...v })} testPrefix="af" disabled={readOnly} labelClass="text-xs" />
      {!readOnly && <Button className="col-span-full justify-self-start" onClick={() => onSave(f)} data-testid="af-save">Save admin fields</Button>}
    </fieldset>
  );
}
